"use client"
import React, { useState, KeyboardEvent } from 'react';
import { typography, spacing } from '@/styles/design-tokens';
interface ComparisonChartRow {
  _key: string;
  rowHeader: string;
  firstOption: React.ReactNode;
  secondOption: React.ReactNode;
}

interface ComparisonChartBlock {
  title?: string;
  text?: string;
  rows?: ComparisonChartRow[];
  optionLabels?: [string, string];
}

export function ComparisonChart({
  title,
  text,
  rows,
  optionLabels = ['Option 1', 'Option 2'],
}: ComparisonChartBlock) {
  const [activeColumnIndex, setActiveColumnIndex] = useState(0);

  if (!rows || rows.length === 0) return null;

  // Toggle active column (for mobile view)
  const toggleActiveColumn = () => {
    setActiveColumnIndex((prev) => (prev === 0 ? 1 : 0));
  };

  // Handle keyboard interaction for toggle buttons
  const onToggleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      toggleActiveColumn();
    }
  };

  return (
    <section
      className={spacing.section}
      role="region"
    >
      <div className={spacing.container}>
        {(title || text) && (
          <div className="mx-auto flex flex-col gap-3 sm:text-center">
            {title && (
              <h2 className={typography.h2}>
                {title}
              </h2>
            )}
            {text && (
              <p className={typography.body}>
                {text}
              </p>
            )}
          </div>
        )}

        <div className="comparison-chart mx-auto max-w-5xl">
          {/* Toggle buttons for small screens */}
          <div
            role="tablist"
            aria-label="Toggle comparison options"
            className="flex justify-center gap-4 mb-4 md:hidden"
          >
            {optionLabels.map((label, idx) => (
              <button
                key={label}
                role="tab"
                aria-selected={activeColumnIndex === idx}
                tabIndex={activeColumnIndex === idx ? 0 : -1}
                onClick={() => setActiveColumnIndex(idx)}
                onKeyDown={onToggleKeyDown}
                className={`${typography.captionSmall} w-full gap-2 text-center rounded-full px-4 py-2  border border-black focus:outline focus:outline-offset-2 focus:outline-black 
                  ${
                    activeColumnIndex === idx
                      ? 'bg-black text-white'
                      : 'bg-gray-light text-gray-dark'
                  }
                  
                `}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Table for comparison */}
          <table
            className="w-full border-collapse"
            role="table"
            aria-label={title ?? 'Comparison chart'}
          >
            <thead className="hidden md:contents pb-6">
              <tr className="text-left">
                <th
                  scope="col"
                  className={`${typography.captionSmall} px-4 py-2 hidden md:table-cell`}
                >
                  {/* Empty top-left corner for row headers */}
                </th>
                {optionLabels.map((label, i) => (
                  <th
                    key={label}
                    scope="col"
                    className={`${typography.captionSmall} text-center px-4 py-2 w-1/2`}
                  >
                    <span className={`${i === 0 ? 'bg-black text-white' : 'bg-white text-black border border-black' } rounded-full px-4 py-2 w-full block`}>{label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={row._key}
                >
                  <th
                    scope="row"
                    id={`${row._key}-rowheader`}
                    className={`${typography.captionSmall} ${rowIndex % 2 === 0 ? 'bg-gray' : ''} px-4 py-3  text-left align-middle rounded-tl-2xl rounded-bl-2xl  md:py-4 md:px-6`}
                  >
                    {row.rowHeader}
                  </th>
                  {/* Desktop: show both columns */}
                  <td
                    aria-labelledby={`${row._key}-rowheader colheader-0`}
                    className={`${typography.bodySmall} ${rowIndex % 2 === 0 ? 'bg-gray' : ''} px-4 py-3 hidden md:table-cell w-1/2 md:py-4 md:px-6`}
                  >
                    {row.firstOption}
                  </td>
                  <td
                    aria-labelledby={`${row._key}-rowheader colheader-1`}
                    className={`${typography.bodySmall} ${rowIndex % 2 === 0 ? 'bg-gray' : ''} px-4 py-3 hidden md:table-cell w-1/2 rounded-tr-2xl rounded-br-2xl md:py-4 md:px-6`}
                  >
                    {row.secondOption}
                  </td>

                  {/* Mobile: show only the active column */}
                  <td
                    colSpan={2}
                    aria-labelledby={`${row._key}-rowheader ${
                      activeColumnIndex === 0
                        ? `colheader-0`
                        : `colheader-1`
                    }`}
                    className={`${typography.bodySmall} ${rowIndex % 2 === 0 ? 'bg-gray' : ''} px-4 py-3 md:hidden rounded-tr-2xl rounded-br-2xl`}
                  >
                    {activeColumnIndex === 0 ? row.firstOption : row.secondOption}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
